import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Bell, Loader2, Check, Ship, FileText, PoundSterling, ShieldCheck, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  new_trip: Ship,
  new_document: FileText,
  new_payslip: PoundSterling,
  sed_status_change: ShieldCheck,
  client_message: MessageSquare,
};

const AccountantNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/notifications`);
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/accountant/notifications/read`);
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      // silent
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="h-6 w-6 animate-spin text-sky-400" /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Notifications</h1>
          <p className="text-slate-400 text-sm mt-1">{unread > 0 ? `${unread} unread` : 'All caught up'}</p>
        </div>
        {unread > 0 && (
          <Button variant="ghost" onClick={markAllRead} className="text-sky-400 hover:text-sky-300 text-xs h-8" data-testid="mark-all-read">
            <Check className="h-3.5 w-3.5 mr-1" />Mark all read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <Bell className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No notifications yet. You'll see alerts here when your clients take action.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {notifications.map(n => {
            const Icon = ICON_MAP[n.type] || Bell;
            return (
              <div key={n.id}
                className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                  n.read ? 'bg-slate-800/50' : 'bg-slate-800 border border-sky-500/20'
                }`} data-testid={`notification-${n.id}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                  n.read ? 'bg-slate-700' : 'bg-sky-600/20'
                }`}>
                  <Icon className={`h-4 w-4 ${n.read ? 'text-slate-500' : 'text-sky-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${n.read ? 'text-slate-400' : 'text-white'}`}>{n.message}</p>
                  {n.client_name && <p className="text-xs text-slate-500 mt-0.5">Client: {n.client_name}</p>}
                </div>
                <span className="text-[10px] text-slate-600 shrink-0 mt-1">{formatTime(n.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountantNotifications;
